'use client';

import SemanticChip from './Utils/SemanticChip';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { useAllRpcLatencies } from 'src/hooks/useRpcLatency';
import { useEffect, useState } from 'react';
import useIsMainnet from 'src/hooks/useIsMainnet';
import { handleRefresh } from 'src/utils/dom';
import { getRpcOptions } from 'src/utils/rpc';
import Input from 'src/components/primitives/Input/Input';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import TextButton from 'src/components/primitives/TextButton/TextButton';

const CUSTOM_RPC_LABEL = 'Custom';

const RpcLatencyIndicator = ({ latency }: { latency: number }) => (
	<SemanticChip
		status={
			latency < 0
				? 'grey'
				: latency < 100
					? 'green'
					: latency < 250
						? 'amber'
						: 'red'
		}
	/>
);

const RpcSelectorPopup = () => {
	const [savedSettings, updateSettings] = useCurrentSettings();

	const [selectedRpcEndpoint, setSelectedRpcEndpoint] = useState(
		savedSettings?.rpcEndpoint
	);

	const [isCustom, setIsCustom] = useState(
		selectedRpcEndpoint?.label === CUSTOM_RPC_LABEL
	);

	const [customStringEntered, setCustomStringEntered] = useState(
		selectedRpcEndpoint?.label === CUSTOM_RPC_LABEL
			? selectedRpcEndpoint?.value
			: ''
	);

	const [isRefreshing, setIsRefreshing] = useState(false);

	const latencies = useAllRpcLatencies();

	const isMainnet = useIsMainnet();

	const rpcOptions = getRpcOptions(isMainnet ? 'mainnet-beta' : 'devnet');

	const handleRpcUpdate = (newRpc: { value: string; label: string }) => {
		setSelectedRpcEndpoint(newRpc);
		setIsCustom(newRpc?.label === CUSTOM_RPC_LABEL);

		if (newRpc.value !== savedSettings.rpcEndpoint.value) {
			setIsRefreshing(true);
			updateSettings({ rpcEndpoint: newRpc });
			handleRefresh('About to refresh to change RPC Node');
		}
	};

	const handleSelectCustom = () => {
		setIsCustom(true);
	};

	const updateToCustomRpcInput = () => {
		handleRpcUpdate({ value: customStringEntered, label: CUSTOM_RPC_LABEL });
	};

	useEffect(() => {
		setSelectedRpcEndpoint(savedSettings?.rpcEndpoint);
		setIsCustom(savedSettings?.rpcEndpoint?.label === CUSTOM_RPC_LABEL);
	}, [savedSettings?.rpcEndpoint?.label]);

	const items: BaseRadioItemProps<string>[] = rpcOptions.map((rpc) => ({
		id: rpc.value,
		value: rpc.value,
		label: (
			<>
				{rpc.label}{' '}
				<span className="flex items-center gap-x-sm typo-b4">
					<RpcLatencyIndicator latency={latencies[rpc.value]?.avg ?? 0} />
					{latencies[rpc.value]?.avg ? `${latencies[rpc.value]?.avg}ms` : 'N/A'}
				</span>
			</>
		),
		className: 'justify-between',
	}));

	return (
		<div className="flex w-[350px] flex-col gap-y-lg p-lg">
			<span className="typo-t3 text-text-default">RPC Endpoint</span>
			<div className="flex flex-col gap-y-sm">
				<RadioGroup.Default
					size="md"
					variant="card"
					className="flex-col gap-y-sm"
					items={items}
					value={
						!customStringEntered ||
						customStringEntered === selectedRpcEndpoint?.value
							? selectedRpcEndpoint?.value
							: undefined
					}
					onValueChange={(value) =>
						handleRpcUpdate({
							value,
							label: rpcOptions.find((rpc) => rpc.value === value)?.label ?? '',
						})
					}
				/>

				<Input
					labelVariant="none"
					size="xs"
					type="text"
					onFocus={handleSelectCustom}
					onChange={(value) => setCustomStringEntered(value)}
					value={customStringEntered}
					placeholder="Enter Custom RPC URL"
					suffix={
						<TextButton
							size="sm"
							disabled={
								!isCustom ||
								!customStringEntered ||
								isRefreshing ||
								customStringEntered === selectedRpcEndpoint?.value
							}
							onClick={updateToCustomRpcInput}
						>
							Save
						</TextButton>
					}
				/>
			</div>
		</div>
	);
};

export default RpcSelectorPopup;
